<script setup>
import { getI18n } from '@/plugins/i18n'
import { menuRoutesEnum } from '@/router'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useVModel } from '@vueuse/core'
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup'

import { formatDateTime, formatTime, isCrossDay } from '@/lib/utils'
import moment from 'moment'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'

import { useDocumentService } from '@/services/document.service'
import { usePortalStore } from '@/stores/portal.store'

import { useGarageStore } from '@/stores/garage.store'
import { garageApi } from '@/apis/garage.api'

const emit = defineEmits(['update:visible', 'save', 'cancel'])
const props = defineProps({
  visible: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object,
    default: null
  },
})

const popupVisible = useVModel(props, 'visible', emit)
const options = computed(() => props.options)

const portalStore = usePortalStore()

const garageStore = useGarageStore()
const stopDates = ref({})
const now = new Date()

const i18n = getI18n()
const selectedMechanicId = ref(null)

const mechanicSections = computed(() => {
  const allMechanics = options.value?.mechanics || []
  const involved = allMechanics.filter(m => m.is_involved)
  const others = allMechanics.filter(m => !m.is_involved)

  if (!involved.length) return [{ label: null, mechanics: allMechanics }]

  return [
    { label: i18n.t('common.mechanicsInDocument'), mechanics: involved },
    { label: i18n.t('common.mechanicsOthers'), mechanics: others },
  ].filter(section => section.mechanics.length)
})

const openTimePicker = (mechanicId) => {
  selectedMechanicId.value = mechanicId
}

const onClose = () => {
  if (!options.value?.cancelButton?.disabled) {
    emit('cancel')
  }
}

const lastKeyPress = ref({ key: '', time: 0 })
const handleKeyDown = (e) => {
  if (e.key === 'Escape') {
    e.stopImmediatePropagation()
    e.preventDefault()

    const now = Date.now()
    const timeDiff = now - lastKeyPress.value.time

    if (e.key === lastKeyPress.value.key && timeDiff < 500) {
      if (e.key === 'Escape') onClose()
    }

    lastKeyPress.value = { key: e.key, time: now }
  }
}

const { extractReferenceData } = useDocumentService()

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown, true)
  props.options?.mechanics.forEach(m => {
    if (!stopDates.value[m.id] && m.active_work.length > 0 && !isCrossDay(m.active_work[0].start)) {
      stopDates.value[m.id] = now
    }
  })
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown, true)
})

const startActivity = (worksheetId, mechanicId) => {
  const data = {
    worksheet: worksheetId,
    mechanic: mechanicId,
    activity_code: garageStore.workActivityEnum.WORKSHEET_PROCESSING,
    start: new Date().toISOString()
  }
  garageApi.createWorkTimeEntryAsync(data).then(() => {
    emit('save')
  })
}

const stopActivity = (workTimeEntryId, dateTime) => {
  const data = {
    end: dateTime.toISOString(),
    is_manual: dateTime !== now
  }
  garageApi.updateWorkTimeEntryAsync(workTimeEntryId, data).then(() => {
    emit('save')
  })
}

const getChipStartDate = (start) => {
  return isCrossDay(start)
    ? formatDateTime(moment.utc(start).local().toDate(), portalStore.selectedLanguage, false)
    : formatTime(moment.utc(start).local().toDate(), portalStore.selectedLanguage)
}

const getChipEndDate = (mechanic) => {
  const start = mechanic.active_work[0].start
  const end = stopDates.value[mechanic.id]
  if (end) {
    return isCrossDay(start)
      ? formatDateTime(moment.utc(end).local().toDate(), portalStore.selectedLanguage, false)
      : formatTime(moment.utc(end).local().toDate(), portalStore.selectedLanguage)
  }
  return formatDateTime(moment.utc(start).local().toDate(), portalStore.selectedLanguage, false).slice(0, -5) + '--:--'
}

const isInvalidStopDate = (mechanic) => {
  const value = stopDates.value[mechanic.id]
  if (!value) return true

  const min = new Date(mechanic.active_work[0].start)
  const max = new Date()

  return value <= min || value > max
}

const getTimeModel = (mechanic) => {
  return computed({
    get() {
      const mechanicId = mechanic.id
      const date = stopDates.value[mechanicId]
      if (!date) return null

      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    set(value) {
      if (!value) return
      const mechanicId = mechanic.id

      const [hours, minutes] = value.split(':')
      const date = stopDates.value[mechanicId] || new Date(mechanic.active_work[0].start)

      date.setHours(Number(hours), Number(minutes), 0, 0)

      stopDates.value[mechanicId] = new Date(date)
    }
  })
}

const popupPosition = {
  my: 'right top',
  at: 'right top',
  of: window,
  offset: '-68 52'
}
</script>

<template>
  <dx-popup
    v-model:visible="popupVisible"
    :drag-enabled="true"
    :show-title="true"
    :hide-on-outside-click="false"
    :show-close-button="options?.showCloseButton || false"
    :close-on-esc="false"
    height="auto"
    :width="options?.width"
    :title="options?.title"
    :position="popupPosition"
  >
    <q-list
      style="max-height: 21.875rem;"
      dense
    >
      <template
        v-for="(section, sectionIndex) in mechanicSections"
        :key="section.label || sectionIndex"
      >
        <q-separator
          v-if="sectionIndex > 0"
          spaced
        />
        <q-item-label
          v-if="section.label"
          header
          class="q-py-xs text-uppercase"
          style="font-size: 0.75rem;"
        >
          {{ section.label }}
        </q-item-label>
        <q-item
          v-for="mechanic in section.mechanics"
          :key="mechanic.id"
          class="q-hoverable"
          style="margin-top: 0.5rem; margin-bottom: 0.5rem; height: 3.125rem;"
        >
          <q-item-section side>
            <div
              v-if="!mechanic.active_work.length > 0"
            >
              <q-btn
                round
                flat
                color="primary"
                icon="mdi-play"
                size="md"
                style="font-size: 0.75rem;"
                @click="startActivity(props.options.worksheetId, mechanic.id)"
              />
            </div>
            <div
              v-else
            >
              <q-btn
                round
                flat
                color="negative"
                icon="mdi-stop-circle-outline"
                size="md"
                :disable="isInvalidStopDate(mechanic)"
                style="font-size: 0.75rem;"
                @click="stopActivity(mechanic.active_work[0].id, stopDates[mechanic.id])"
              >
                <q-tooltip v-if="isInvalidStopDate(mechanic)">
                  {{ $t('common.invalidDateRange') }}
                </q-tooltip>
              </q-btn>
            </div>
          </q-item-section>
          <q-item-section>
            <q-item-label
              v-if="!(mechanic.active_work.length > 0)"
              lines="1"
              style="display: flex;"
            >
              {{ mechanic.name }} {{ mechanic.name_ext }}
            </q-item-label>
            <q-item-label
              v-else
              lines="1"
              style="display: flex;"
            >
              {{ mechanic.name }} {{ mechanic.name_ext }} -&nbsp;
              <reference-link
                :data="{
                  series: mechanic.active_work[0].worksheet.series,
                  number: mechanic.active_work[0].worksheet.number_mnemonic
                }"
                :text-fields="['prefix', 'series', 'number']"
                :query-fields="['series', 'number']"
                :target="menuRoutesEnum.WORKSHEETS"
              >
                <information-tool-tip :data="extractReferenceData({...mechanic.active_work[0].worksheet, type: portalStore.documentTypesEnum.GARAGE_WORKSHEET })" />
              </reference-link>
            </q-item-label>

            <q-item-label
              v-if="mechanic.active_work.length > 0"
              style="margin: 0;"
            >
              <q-chip
                size="sm"
                text-color="white"
                :label="getChipStartDate(mechanic.active_work[0].start)"
                color="grey"
                style="margin-left: 0;"
              />
              -
              <q-chip
                size="sm"
                clickable
                text-color="white"
                :label="getChipEndDate(mechanic)"
                icon-right="mdi-pencil"
                color="grey"
                @click="openTimePicker(mechanic.id)"
              >
                <q-popup-proxy
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-time
                    v-model="getTimeModel(mechanic).value"
                    format24h
                  />
                </q-popup-proxy>
              </q-chip>
            </q-item-label>
            <q-item-label
              v-else
            >
              {{ $t('common.available') }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-list>

    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{
        text: i18n.t('common.close'),
        onClick: onClose
      }"
    />
  </dx-popup>
</template>

<style lang="scss">
.q-hoverable:hover {
  background-color: rgba(0, 0, 0, 0.05);
  cursor: default;
}

:deep(.dx-calendar) {
  display: none !important;
}
</style>
